import React from "react";
import styled from "styled-components";
import { getScaleNotes } from "../utils/chordSuggestions";
import { noteNameToMidi } from "../utils/chordUtils";

interface DroneKeyboardProps {
  selectedKey: string;
  selectedMode: "minor" | "major";
  droneNotes: string[]; // Array of 1 or 2 pitch classes (e.g., ["C", "G"])
  droneMode: "mono" | "duo";
  disabled?: boolean;
  onNoteClick: (note: string) => void;
}

const DroneKeyboard: React.FC<DroneKeyboardProps> = ({
  selectedKey,
  selectedMode,
  droneNotes,
  droneMode: _droneMode,
  disabled = false,
  onNoteClick,
}) => {
  // Get scale notes for coloring
  const scaleMode: "major" | "naturalMinor" | "harmonicMinor" =
    selectedMode === "major" ? "major" : "naturalMinor";
  const scaleNotes = getScaleNotes(selectedKey, scaleMode);
  const scalePitchClasses = new Set(scaleNotes.map((n) => n % 12));
  const stripOctave = (noteName: string): string =>
    noteName.replace(/-?\d+$/, "");
  const notePitchClass = (noteName: string): number =>
    noteNameToMidi(stripOctave(noteName), 4) % 12;
  const selectedPitchClasses = new Set(
    droneNotes.map((note) => notePitchClass(note))
  );

  // Natural notes (white keys)
  const naturalNotes = ["C", "D", "E", "F", "G", "A", "B"];

  // Accidental notes (black keys) - positioned between naturals
  const accidentals = [
    { note: "Db", between: ["C", "D"] },
    { note: "Eb", between: ["D", "E"] },
    { note: "Gb", between: ["F", "G"] },
    { note: "Ab", between: ["G", "A"] },
    { note: "Bb", between: ["A", "B"] },
  ];

  // Check if a note is in the scale
  const isNoteInScale = (noteName: string): boolean => {
    try {
      return scalePitchClasses.has(notePitchClass(noteName));
    } catch {
      return false;
    }
  };

  // Check if a note is currently selected
  const isNoteSelected = (noteName: string): boolean => {
    return selectedPitchClasses.has(notePitchClass(noteName));
  };

  return (
    <DroneKeyboardWrapper id="drone-keyboard">
      <KeyboardContainer id="keyboard-container">
        {/* Keyboard */}
        <Keyboard id="keyboard">
          {/* Accidental notes row (top) */}
          <AccidentalsRow id="accidentals-row">
            {accidentals.map((acc) => {
              const inScale = isNoteInScale(acc.note);
              const selected = isNoteSelected(acc.note);

              // Calculate position: find which naturals this accidental is between
              const firstNaturalIndex = naturalNotes.indexOf(acc.between[0]);
              const secondNaturalIndex = naturalNotes.indexOf(acc.between[1]);
              // Position at the midpoint between the two naturals
              const position = (firstNaturalIndex + secondNaturalIndex) / 2;

              return (
                <AccidentalKey
                  key={acc.note}
                  id={`key-${acc.note.toLowerCase()}`}
                  $inScale={inScale}
                  $selected={selected}
                  $disabled={disabled}
                  $position={position}
                  onClick={() => !disabled && onNoteClick(acc.note)}
                >
                  {acc.note}
                </AccidentalKey>
              );
            })}
          </AccidentalsRow>

          {/* Natural notes row (bottom) */}
          <NaturalsRow id="naturals-row">
            {naturalNotes.map((note) => {
              const inScale = isNoteInScale(note);
              const selected = isNoteSelected(note);

              return (
                <NaturalKey
                  key={note}
                  id={`key-${note.toLowerCase()}`}
                  $inScale={inScale}
                  $selected={selected}
                  $disabled={disabled}
                  onClick={() => !disabled && onNoteClick(note)}
                >
                  {note}
                </NaturalKey>
              );
            })}
          </NaturalsRow>
        </Keyboard>
      </KeyboardContainer>
    </DroneKeyboardWrapper>
  );
};

export default DroneKeyboard;

// Styled Components
const DroneKeyboardWrapper = styled.div`
  width: 100%;
  border-radius: 8px;
`;

const KeyboardContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
`;

const Keyboard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  height: 76px;
`;

const AccidentalsRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0 10px;
  height: 30px;
  margin-bottom: 8px;
`;

const AccidentalKey = styled.div<{
  $inScale: boolean;
  $selected: boolean;
  $disabled: boolean;
  $position: number;
}>`
  background-color: ${(props) =>
    props.$selected ? (props.$inScale ? "#23D18E" : "#E9A499") : "transparent"};
  color: ${(props) =>
    props.$selected ? "#34313B" : props.$inScale ? "#23D18E" : "#E9A499"};
  border: ${(props) =>
    props.$selected
      ? "2px solid #34313B"
      : props.$inScale
      ? "2px solid #23D18E"
      : "2px solid #E9A499"};
  border-radius: 4px;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  user-select: none;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  width: calc((100% - 20px - 12px) / 7);
  height: 30px;
  text-align: center;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: ${(props) => {
    // Calculate position: start at 10px padding, then position based on natural key positions
    // Each natural is (100% - 20px padding - 12px gaps) / 7 wide, with 2px gaps
    const keyWidth = `calc((100% - 20px - 12px) / 7)`;
    const gapWidth = `2px`;
    return `calc(10px + ${props.$position} * (${keyWidth} + ${gapWidth}) + ${keyWidth} / 2)`;
  }};
  transform: translateX(-50%);

  &:hover {
    ${(props) => !props.$disabled && "opacity: 0.8;"}
  }

  &:active {
    ${(props) => !props.$disabled && "opacity: 0.6;"}
  }
`;

const NaturalsRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 0 10px;
  height: 30px;
`;

const NaturalKey = styled.div<{
  $inScale: boolean;
  $selected: boolean;
  $disabled: boolean;
}>`
  background-color: ${(props) =>
    props.$selected ? (props.$inScale ? "#23D18E" : "#E9A499") : "transparent"};
  color: ${(props) =>
    props.$selected ? "#34313B" : props.$inScale ? "#23D18E" : "#E9A499"};
  border: ${(props) =>
    props.$selected
      ? "2px solid #34313B"
      : props.$inScale
      ? "2px solid #23D18E"
      : "2px solid #E9A499"};
  border-radius: 4px;
  font-family: "Dogica Pixel", monospace;
  font-size: 12px;
  font-weight: 600;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  user-select: none;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  width: calc((100% - 20px - 12px) / 7);
  height: 30px;
  text-align: center;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    ${(props) => !props.$disabled && "opacity: 0.8;"}
  }

  &:active {
    ${(props) => !props.$disabled && "opacity: 0.6;"}
  }
`;
